// Referee / fouls / diving / discipline config (§7). All referee constants live here so the sim stays
// magic-number-free and replay builds cfg from JSON. Frozen; pure data.

export interface RefereeConfig {
  // §7.1 foul severity
  readonly F_MIN: number; // 2 — impulse floor for force01
  readonly F_MAX: number; // 12 — impulse ceiling
  readonly W_FORCE: number; // 0.35
  readonly W_BALLFIRST: number; // 0.30
  readonly W_BEHIND: number; // 0.15
  readonly W_ACTION: number; // 0.20
  readonly actionW: { readonly TACKLE_STAND: number; readonly TACKLE_SLIDE: number; readonly SHOVE: number; readonly PUNCH_ACTIVE: number; readonly OTHER: number };
  readonly T_FOUL: number; // 0.35 — < legal
  readonly T_YELLOW: number; // 0.62 — < foul, else foul+yellow
  readonly T_RED: number; // 0.85 — ≥ red
  readonly PUNCH_SEV: number; // 0.86 — landed-punch severity floor
  readonly FOUL_KNOCKBACK: number; // D55 — impulse (m/s) throwing a whistled foul's victim off his feet
  readonly CLEAN_SLIDE_MULT: number; // 0.25 — clean ball-first slide, behind01 < CLEAN_SLIDE_BEHIND
  readonly CLEAN_SLIDE_BEHIND: number; // 0.3
  // §7.2 detection
  readonly CONE_HALF: number; // 60° in radians — ref sight cone half-angle
  readonly CONE_DIST: number; // 38 m
  readonly CLARITY_PLATEAU: number; // 15 m — in-view ≤ this ⇒ clarity 1.0
  readonly OCCLUSION_SHORTEN: number; // 0.5 m — sightline shortened at the incident end (no self-occlusion)
  readonly LINESMAN_BASE: number; // 0.18
  readonly K_SEV: number; // 1.2
  readonly K_REF: number; // 1.15
  // §7.3 advantage
  readonly ADV_WINDOW: number; // 90
  readonly ADV_KEEP: number; // 45
  // §7.4 diving
  readonly DIVE_WINDOW: number; // 15
  readonly T_AWARD: number; // 0.62
  readonly T_CAUGHT: number; // 0.30
  readonly I_MIN: number; // 0.25
  readonly I_CATCH: number; // 0.45
  readonly DIVE_REP_PENALTY: number; // 0.25
  readonly DIVE_REP_MIN: number; // 0.2
  // §7.4 cried-wolf gate
  readonly R_WOLF: number; // 0.5
  readonly C_WOLF: number; // 0.6
  // §7.5 movement
  readonly BEHIND_PLAY_OFFSET: number; // diagonal-follow offset behind the ball (m)
  readonly REF_MIN_DIST: number; // 6 — min distance from the ball
  readonly REF_MAX_SPEED: number; // 7 m/s
  readonly SCAN_PERIOD: number; // 60 — mean ticks between look-away scans
  readonly SCAN_JITTER: number; // 20 — ± jitter (drawn from state.rng)
  readonly SCAN_DUR: number; // 20 — scan sweep duration
  readonly SUSPENSION_TICKS: number; // D65c — a red card benches the player this many ticks, then he RETURNS (600 = 20 s)
}

export const DEFAULT_REFEREE: RefereeConfig = Object.freeze({
  F_MIN: 2,
  F_MAX: 12,
  W_FORCE: 0.35,
  W_BALLFIRST: 0.3,
  W_BEHIND: 0.15,
  W_ACTION: 0.2,
  actionW: Object.freeze({ TACKLE_STAND: 0.15, TACKLE_SLIDE: 0.3, SHOVE: 0.55, PUNCH_ACTIVE: 1.0, OTHER: 0.1 }),
  T_FOUL: 0.35,
  T_YELLOW: 0.62,
  T_RED: 0.85,
  PUNCH_SEV: 0.86,
  FOUL_KNOCKBACK: 4.5, // D55: theatrical — the victim visibly goes flying
  CLEAN_SLIDE_MULT: 0.25,
  CLEAN_SLIDE_BEHIND: 0.3,
  CONE_HALF: (60 * Math.PI) / 180,
  CONE_DIST: 38,
  CLARITY_PLATEAU: 15,
  OCCLUSION_SHORTEN: 0.5,
  LINESMAN_BASE: 0.18,
  K_SEV: 1.2,
  K_REF: 1.15,
  ADV_WINDOW: 90,
  ADV_KEEP: 45,
  DIVE_WINDOW: 15,
  T_AWARD: 0.62,
  T_CAUGHT: 0.3,
  I_MIN: 0.25,
  I_CATCH: 0.45,
  DIVE_REP_PENALTY: 0.25,
  DIVE_REP_MIN: 0.2,
  R_WOLF: 0.5,
  C_WOLF: 0.6,
  BEHIND_PLAY_OFFSET: 4,
  REF_MIN_DIST: 6,
  REF_MAX_SPEED: 7,
  SCAN_PERIOD: 60,
  SCAN_JITTER: 20,
  SCAN_DUR: 20,
  SUSPENSION_TICKS: 600, // D65c: 20 s at 30 Hz — a red card is a timed suspension, not a full send-off
});
